import {
  ManualAdjustmentKey,
  ORCHESTRATE_AUTOFILL_PROPERTY_API,
  OrderFeature,
  schSourceType,
} from '@aloft/toolkit-shared';
import { Opts } from '@aloft/toolkit-sui';

import { Control, Path, UseFormGetValues, UseFormSetValue } from 'react-hook-form';
import { z } from 'zod';

export type PropertyGridType = 'FeatureGrid' | 'AdjustmentsGrid';

export type PropertyGridColumnSectionComponent = React.ComponentType<PropertyGridSectionProps>;

const schColumnType = schSourceType.or(z.literal('property'));
export type ColumnType = z.infer<typeof schColumnType>;

export const schPropertyGridColumnDefinition = z.object({
  propertyId: z.string(),
  type: schColumnType,
  propertyData: ORCHESTRATE_AUTOFILL_PROPERTY_API.GET.RESPONSE.optional(),
});

export type PropertyGridColumnDefinition = z.infer<typeof schPropertyGridColumnDefinition>;

export const schPropertyGridForm = z.object({
  columns: z.array(schPropertyGridColumnDefinition),
  dataSources: z.array(schSourceType),
});

export type PropertyGridForm = z.infer<typeof schPropertyGridForm>;

export type PropertyGridRowFieldType =
  | 'dataSourceValue'
  | 'label'
  | 'editableValue'
  | 'editableArvValue'
  | 'readOnlyArvValue'
  | 'readOnlyValue'
  | 'adjustment';

export type PropertyGridSectionProps = {
  columnIndex: number;
  fieldTypes: PropertyGridRowFieldType[];
  gridType: PropertyGridType;
  feature?: OrderFeature;
  isRemovable?: boolean;
  hasSeparateArvValue?: boolean;
  onRemove?: () => void;
  control: Control<PropertyGridForm>;
  getValues: UseFormGetValues<PropertyGridForm>;
  setValue: UseFormSetValue<PropertyGridForm>;
};

export type PropertyGridFieldName = Path<PropertyGridForm['columns'][number]['propertyData']>;

export type PropertyGridField<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  T = any,
  U extends string | number = T extends string | number ? T : string,
> = {
  fieldName: PropertyGridFieldName;
  label: string;
  manualAdjustmentKey?: ManualAdjustmentKey;
  options?: Opts<U>; // For enum fields
  includeEmptyOption?: boolean;
  autofillSubjectValue?: boolean;
  tooltipContent?: string;
  toDisplayValue?: (value: T | undefined, propertyId: string) => string | undefined;
  toEditValue?: (value: T | undefined, propertyId: string) => string | undefined;
  toValue?: (value: string | undefined, propertyId: string) => T | undefined;
};
